import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { findAllParent, findMenuItem } from '../helper/utils';
import { User } from 'src/app/core/models/users.model';
import { MenuItem } from '../models/menu.model';
import { getMenuItems } from '../config/menu-meta';
import { EventService } from '../../../core/services/event.service';
import { EventType } from 'src/app/core/constants/events';
import { LayoutType, SideBarSize } from '../config/layout.model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

declare var require: any;
const FileSaver = require('file-saver');
// @ts-ignore
declare function chat_zopim(active: boolean);

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() includeUserProfile: boolean = false;

  activeMenuItems: string[] = [];
  loggedInUser: User | null = {};
  menuItems: MenuItem[] = [];
  role = '';
  @Input() layoutType: string = 'vertical';
  @Input() leftbarSize!: string;
  @Input() layoutWidth!: string;
  @Input() layoutColor!: string;
  private capabilitiesUpdateSubscription?: Subscription;

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private eventService: EventService,
    private translateService: TranslateService
  ) {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        setTimeout(() => {
          this._activateMenu();
        }, 0);
        this.hideMenu();
      }
    });
    const storedSidebarState = localStorage.getItem('cayc-ui-sidebar');
    if (storedSidebarState) {
      this.changeLeftSidebarSize(storedSidebarState);
    }
    const storedColorScheme = localStorage.getItem('cayc-ui-theme');
    this.layoutColor = storedColorScheme ? storedColorScheme : 'light';
  }

  ngOnInit(): void {
    this.initMenu();
    this.loggedInUser = this.authService.currentUser();
    const storedSidebarState = localStorage.getItem('cayc-ui-sidebar');
    this.changeLeftSidebarSize(
      storedSidebarState ? storedSidebarState : 'default'
    );
    setInterval(() => {
      const storedColorScheme = localStorage.getItem('cayc-ui-theme');
      if (storedColorScheme) {
        this.layoutColor = storedColorScheme;
      }
    }, 100);
    // Suscribirse al evento de actualización de capacidades
    this.capabilitiesUpdateSubscription = this.eventService
      .on(EventType.USER_CAPABILITIES_UPDATED)
      .subscribe(() => {
        this.refreshMenu();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._activateMenu();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.capabilitiesUpdateSubscription) {
      this.capabilitiesUpdateSubscription.unsubscribe();
    }
  }

  /**
   * Inicializar el menú
   */
  initMenu(): void {
    const currentUser = sessionStorage.getItem('cayc-user');
    if (currentUser !== null) {
      const userCapabilitiesRaw = JSON.parse(currentUser).capabilities || [];
      const userCapabilities = userCapabilitiesRaw.map((cap: any) => {
        if (typeof cap === 'object' && cap.key) {
          return cap.key;
        }
        return cap;
      });
      this.menuItems = getMenuItems(this.translateService).filter(
        (item) =>
          !item.capabilities ||
          item.capabilities.length === 0 ||
          item.capabilities.some((cap) => userCapabilities.includes(cap))
      );
    }
  }

  /**
   * Refrescar el menú cuando se actualizan las capacidades del usuario
   */
  refreshMenu(): void {
    this.authService.clearUserCache();
    this.loggedInUser = this.authService.currentUser();
    this.initMenu();
    this._activateMenu();
  }

  /**
   * Devolver true/false si el menú tiene hijos
   * @param menu Menú
   */
  hasSubmenu(menu: MenuItem): boolean {
    return !!menu.children;
  }

  /**
   * Buscar un menú por su URL
   * @param menuItems Lista de menús
   * @param url URL a buscar
   * @returns Menú que coincide con la URL
   */
  private findMenuItemByUrl(
    menuItems: MenuItem[],
    url: string
  ): MenuItem | null {
    for (const menu of menuItems) {
      if (menu.url && this.isUrlMatch(menu.url, url)) {
        return menu;
      }
      if (menu.children) {
        const found = this.findMenuItemByUrl(menu.children, url);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Encontrar todos los menús padre de un menú dado
   * @param menuItems Lista de menús
   * @param targetMenu Menú objetivo
   * @returns Array de claves de menús padre
   */
  private findAllParentMenus(
    menuItems: MenuItem[],
    targetMenu: MenuItem
  ): string[] {
    const parents: string[] = [];

    const findParent = (
      items: MenuItem[],
      target: MenuItem,
      currentPath: string[] = []
    ): boolean => {
      for (const item of items) {
        const newPath = [...currentPath, item.key!];

        // Si encontramos el menú objetivo
        if (item.key === target.key) {
          // Agregar todos los padres excepto el mismo menú
          parents.push(...currentPath);
          return true;
        }

        // Si tiene hijos, buscar recursivamente
        if (item.children && item.children.length > 0) {
          if (findParent(item.children, target, newPath)) {
            return true;
          }
        }
      }
      return false;
    };

    findParent(menuItems, targetMenu);
    return parents;
  }

  /**
   * Verificar si una URL coincide con la ruta actual
   * @param menuUrl URL del menú
   * @param currentUrl URL actual
   * @returns true si coinciden
   */
  private isUrlMatch(menuUrl: string, currentUrl: string): boolean {
    if (!menuUrl || !currentUrl) {
      return false;
    }

    const normalizedMenuUrl = menuUrl.trim().replace(/\/$/, '') || '/';
    const normalizedCurrentUrl = currentUrl.trim().replace(/\/$/, '') || '/';

    const menuUrlNormalized = normalizedMenuUrl.startsWith('/')
      ? normalizedMenuUrl
      : '/' + normalizedMenuUrl;
    const currentUrlNormalized = normalizedCurrentUrl.startsWith('/')
      ? normalizedCurrentUrl
      : '/' + normalizedCurrentUrl;

    return menuUrlNormalized === currentUrlNormalized;
  }

  /**
   * Activar el menú
   */
  _activateMenu(): void {
    if (!this.menuItems || this.menuItems.length === 0) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0];

    if (this.isUrlMatch('/notifications', currentUrl)) {
      this.activeMenuItems = [];
      this.menuItems.forEach((menu: MenuItem) => {
        menu.collapsed = true;
      });
      return;
    }

    let activeMenuItem: MenuItem | null = null;

    activeMenuItem = this.findMenuItemByUrl(this.menuItems, currentUrl);

    if (!activeMenuItem) {
      const div = document.getElementById('side-menu');
      if (div) {
        let items: any = div.getElementsByClassName('side-nav-link-ref');
        for (let i = 0; i < items.length; ++i) {
          const itemPath = items[i].getAttribute('href') || items[i].pathname;
          if (itemPath && this.isUrlMatch(itemPath, currentUrl)) {
            const mid = items[i].getAttribute('data-menu-key');
            if (mid) {
              activeMenuItem = findMenuItem(this.menuItems, mid);
              break;
            }
          }
        }
      }
    }

    if (activeMenuItem && activeMenuItem.key) {
      const parentKeys = this.findAllParentMenus(
        this.menuItems,
        activeMenuItem
      );
      const matchingObjs = [activeMenuItem.key, ...parentKeys];

      this.activeMenuItems = matchingObjs;

      this.menuItems.forEach((menu: MenuItem) => {
        menu.collapsed = !matchingObjs.includes(menu.key!);
      });
    }
  }

  /**
   * Abrir/cerrar el menú con submenús
   * @param menuItem Menú seleccionado
   * @param collapse Instancia de collapse
   */
  toggleMenuItem(menuItem: MenuItem, collapse: NgbCollapse): void {
    collapse.toggle();
    let openMenuItems: string[];
    if (!menuItem.collapsed) {
      openMenuItems = [
        menuItem['key'],
        ...findAllParent(this.menuItems, menuItem),
      ];
      this.menuItems.forEach((menu: MenuItem) => {
        if (!openMenuItems.includes(menu.key!)) {
          menu.collapsed = true;
        }
      });
    }
  }

  /**
   * Ocultar la barra de menú
   */
  hideMenu(): void {
    document.body.classList.remove('sidebar-enable');
    // Asegurar que el body no tenga overflow oculto si estaba activo
    if (window.innerWidth <= 768) {
      document.body.style.overflow = '';
    }
  }

  /**
   * Descargar manual de usuario
   * @param type Tipo de archivo, en este caso siempre es 'manual-usuario-direct-cayc.pdf'
   */
  downloadFile(type: string) {
    const pdfUrl = `../../assets/docs/${type}`;
    FileSaver.saveAs(pdfUrl, type.split('.')[0]);
  }

  /**
   * Activar el chat de soporte
   */
  toggleChat() {
    chat_zopim(true);
  }

  /**
   * Abrir/cerrar el menú lateral
   */
  toggleSidebarMenu(): void {
    const newSidebarState =
      this.leftbarSize === 'default' ? 'condensed' : 'default';
    this.changeLeftSidebarSize(newSidebarState);
  }

  /**
   * Cambiar el ancho del menú lateral
   * @param size Ancho del menú lateral
   */
  changeLeftSidebarSize(size: string): void {
    this.leftbarSize = size;
    localStorage.setItem('cayc-ui-sidebar', size);
    if (this.layoutType === LayoutType.LAYOUT_VERTICAL) {
      this.eventService.broadcast(
        EventType.CHANGE_LEFTBAR_SIZE,
        size as SideBarSize
      );
    }
  }
}
